import { BaseElement } from 'base';
import { bus } from 'bus';

const TOPIC = 'locale-selector:active';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  "input:not([disabled]):not([type='hidden'])",
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])"
].join(', ');

const SCRIM = [
  '.locale-selector__dialog',
  '.locale-selector__scroll-area',
  '.locale-selector__viewport'
].join(', ');

const isScrim = target => target instanceof Element && target.matches(SCRIM);

class LocaleSelector extends BaseElement {
  #active = null;
  #saving = false;
  #pressedScrim = false;

  onConnect() {
    this.dialog = this.q('.locale-selector__dialog');
    if (!this.dialog) return;

    this.listen(this.dialog, 'cancel', this.#onCancel);
    this.listen(this.dialog, 'close', this.#onClose);
    this.listen(this.dialog, 'keydown', this.#onKeydown);
    this.listen(this.dialog, 'pointerdown', this.#onPointerdown);
    this.listen(this.dialog, 'click', this.#onClick);
    const form = this.q('.locale-selector__form');
    if (form) this.listen(form, 'submit', this.#onSubmit);
    this.listen(window, 'pageshow', this.#onPageshow);

    customElements.whenDefined('action-button').then(() => {
      if (!this.isConnected) return;
      this.q('.locale-selector__close action-button')?.run(() =>
        this.dismiss()
      );
    });

    customElements.whenDefined('select-field').then(() => {
      if (!this.isConnected) return;
      this.language = this.q('.locale-selector__language');
      this.country = this.q('.locale-selector__country');
      if (!this.language || !this.country) return;
      this.cleanup(
        this.country.onChange(({ value }) => this.#constrain(value))
      );
      if (this.dialog.open) this.#constrain(this.country.value);
    });

    this.subscribe(TOPIC, this.#reconcile);
  }

  dismiss() {
    bus.publish(TOPIC, null);
  }

  #reconcile(active) {
    this.#active = active;
    if (active !== this.id) {
      this.#close();
      return;
    }
    if (this.dialog.matches(':modal')) return;
    if (this.dialog.open) this.dialog.close();
    this.#constrain(this.country?.value);
    this.dialog.showModal();
  }

  #constrain(country) {
    if (!this.language || !this.country) return;
    const codes = this.country.optionData(country)?.languages ?? '';
    const supported = codes
      .split(' ')
      .filter(code => code && this.language.has(code));
    this.language.limit(supported.length ? supported : null);
    if (supported.length && !supported.includes(this.language.value))
      this.language.select(supported[0]);
  }

  #close() {
    if (!this.dialog.open) return;
    this.#discard();
    this.dialog.close();
  }

  #discard() {
    this.#endSaving();
    for (const select of this.qa('select-field')) select.reset?.();
    this.language?.limit(null);
  }

  #onCancel(event) {
    event.preventDefault();
    this.dismiss();
  }

  #onClose() {
    if (this.dialog.open || this.#active !== this.id) return;
    this.#discard();
    bus.publish(TOPIC, null);
  }

  #onKeydown(event) {
    if (event.key !== 'Tab') return;

    const focusable = this.qa(FOCUSABLE).filter(
      element => element.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable.at(-1);
    const active = document.activeElement;
    const inside = active !== this.dialog && this.dialog.contains(active);

    if (event.shiftKey && (!inside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  #onPointerdown(event) {
    this.#pressedScrim = isScrim(event.target);
  }

  #onClick(event) {
    const onScrim = this.#pressedScrim && isScrim(event.target);
    this.#pressedScrim = false;
    if (onScrim) this.dismiss();
  }

  #onSubmit(event) {
    if (this.#saving) {
      event.preventDefault();
      return;
    }
    this.#saving = true;
    this.#saveButton()?.setLoading?.(true);
  }

  #onPageshow(event) {
    if (!event.persisted) return;
    this.#endSaving();
    if (this.dialog.open) this.dismiss();
  }

  #endSaving() {
    this.#saving = false;
    this.#saveButton()?.setLoading?.(false);
  }

  #saveButton() {
    return this.q('.locale-selector__submit');
  }
}

if (!customElements.get('locale-selector'))
  customElements.define('locale-selector', LocaleSelector);
